import {CommonModule} from '@angular/common';
import {HttpErrorResponse} from '@angular/common/http';
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Optional,
  Output,
  SimpleChanges,
} from '@angular/core';
import {ButtonModule} from '@aztrix/components/button';
import {OtpVerificationModule} from '@aztrix/components/otp-verification';
import {IS_WEB_COMPONENT, MatomoService} from '@aztrix/helpers';
import {IconsModule} from '@aztrix/icons';
import {AgreementVerificationType} from '@aztrix/models';
import {
  AgreementRepresentation,
  AgreementsService,
  ProposalLanguageRepresentation,
  ProposalRepresentation,
} from '@aztrix/sdk';
import {TranslatePipe} from '@aztrix/translate';
import {getTime} from 'date-fns';
import {first, Observable, tap} from 'rxjs';

@Component({
  selector: 'ax-agreement-verification',
  templateUrl: './agreement-verification.component.html',
  styleUrls: ['./agreement-verification.component.scss'],
  standalone: true,
  imports: [CommonModule, TranslatePipe, IconsModule, ButtonModule, OtpVerificationModule],
})
export class AgreementVerificationComponent implements OnChanges, OnInit, OnDestroy {
  @Input() proposal?: ProposalRepresentation | null;
  @Input() agreement?: AgreementRepresentation;
  @Input() language?: ProposalLanguageRepresentation;
  @Input() isExternal: boolean;
  @Input() autoVerification = true;

  @Output() verified = new EventEmitter<AgreementRepresentation>();
  @Output() verificationError = new EventEmitter<HttpErrorResponse>();

  AgreementVerificationType = AgreementVerificationType;
  @Output() resendSuccess = new EventEmitter<AgreementRepresentation>();

  _verified = false;
  success = false;
  resendSuccessful = false;
  verificationValue: string | undefined;
  verificationCode: string | undefined;
  verificationAttemptExpirationTimestamp: number | undefined;
  verificationAttemptTimestamp: number | undefined;
  private _interval: NodeJS.Timeout | undefined;
  private _urlSearchParams = new URLSearchParams(window.location.search);

  constructor(
    private _agreements: AgreementsService,
    private _changeDetector: ChangeDetectorRef,
    private _matomo: MatomoService,
    @Optional() @Inject(IS_WEB_COMPONENT) isWebComponent = false
  ) {
    if (isWebComponent) {
      this.verificationCode = this._urlSearchParams.get('aztrixVerificationCode') || undefined;
    } else {
      this.verificationCode = this._urlSearchParams.get('verificationCode') || undefined;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.proposal || changes.agreement) {
      this._verified =
        !!this.proposal?.verification &&
        this.proposal.verification !== ProposalRepresentation.VerificationEnum.NONE &&
        !!this.agreement?.verified &&
        !!this.agreement.active;
      this.verificationValue = this.agreement?.verificationValue;
      this.verificationAttemptExpirationTimestamp =
        this.agreement?.verificationAttemptExpirationTimestamp;
      this.verificationAttemptTimestamp = this.agreement?.verificationAttemptTimestamp;
    }
  }

  ngOnInit(): void {
    // sendVerification if the agreement is not verified and no verification attempt has been made
    if (!this._verified && !this.verificationAttemptExpirationTimestamp) {
      this.sendVerification(this.verificationValue || '');
    }
    // if the agreement is not verified and a verification attempt has been made
    // resend verification code after verificationAttemptExpirationTimestamp is passed
    if (!this._interval && this.verificationAttemptExpirationTimestamp) {
      this._interval = setInterval(() => {
        if (
          !this.resendSuccessful &&
          (this.verificationAttemptExpirationTimestamp || 0) < Date.now()
        ) {
          this.retryVerification(this.verificationValue || '')
            .pipe(first())
            .subscribe();
        }
        if (
          !this.resendSuccessful &&
          (this.verificationAttemptExpirationTimestamp || 0) > Date.now()
        ) {
          this._changeDetector.detectChanges();
        } else {
          clearInterval(this._interval);
          this._interval = undefined;
        }
      }, 1000);
    }
  }

  ngOnDestroy(): void {
    clearInterval(this._interval);
  }

  verification = (code: string): Observable<any> => {
    return this._agreements
      .verify(this.agreement?.id || '', this.agreement?.agreementCode, code)
      .pipe(
        tap({
          error: (error) => {
            if (error.error === 'AGREEMENT_ALREADY_VERIFIED') {
              this._verified = true;
              this.verified.emit(this.agreement);
            }
            this.verificationError.emit(error);
            this.removeVerificationCodeFromUrl();
          },
          next: (agreement) => {
            // Track if the manageLink was used by checking if the code is the same as the one in the URL
            this._matomo.trackEvent(
              'subscribe',
              'verification',
              code === this.verificationCode ? 'manual' : 'manage-link'
            );

            this._verified = true;
            this.success = true;
            this.verified.emit(agreement);
            this.removeVerificationCodeFromUrl();
          },
        })
      );
  };

  retryVerification = (verificationValue: string): Observable<any> => {
    return this._agreements
      .sendVerification(this.agreement?.id || '', this.agreement?.agreementCode, {
        verificationValue,
        language: this.language?.languageCode,
      })
      .pipe(
        tap({
          next: (agreement) => {
            this.resendSuccessful = true;
            this.resendSuccess.emit(agreement);
            this._changeDetector.detectChanges();
            this._matomo.trackEvent('subscribe', 'verification', 'resend');
          },
          error: (error) => {
            this._matomo.trackError(this.proposal?.id || '', this.agreement?.id || '', error);
            if (error.error === 'AGREEMENT_ALREADY_VERIFIED') {
              this._verified = true;
              this.verificationError.emit(error);
            }
          },
        })
      );
  };

  sendVerification(verificationValue: string): void {
    this._agreements
      .sendVerification(this.agreement?.id || '', this.agreement?.agreementCode, {
        verificationValue,
        language: this.language?.languageCode,
      })
      .pipe(first())
      .subscribe((agreement) => {
        this.agreement = agreement;
      });
  }

  verificationComplete(agreement: AgreementRepresentation) {
    this.verified.emit(agreement);
  }

  removeVerificationCodeFromUrl() {
    if (this._urlSearchParams.has('aztrixVerificationCode')) {
      this._urlSearchParams.delete('aztrixVerificationCode');
    }
    if (this._urlSearchParams.has('verificationCode')) {
      this._urlSearchParams.delete('verificationCode');
    }
    window.history.replaceState(
      {},
      '',
      `${window.location.pathname}?${this._urlSearchParams.toString()}`
    );
    this.verificationCode = undefined;
  }

  get resendMinutes() {
    if (
      this.verificationAttemptExpirationTimestamp &&
      this.verificationAttemptExpirationTimestamp > 0
    ) {
      const currentTime = getTime(new Date());
      const differenceInMinutes = Math.ceil(
        (this.verificationAttemptExpirationTimestamp - currentTime) / 1000 / 60
      );
      return differenceInMinutes;
    }
    return 0;
  }
}
